import log from '@/lib/logger';
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from '@/components/ui/dropdown-menu';
import {Button} from '@/components/ui/button';
import {AlignJustify, Pencil, Trash2, Printer, CheckCircle2, RotateCcw, Mail} from 'lucide-react';
import {Text} from "@/components/tailwind/text.tsx";
import {OrderDetails} from "@/models/order.ts";
import ConfirmationDialog, {useConfirmationDialog} from "@/components/confirmation-dialog";
import {useOrdersTranslation} from "@/i18n/hooks";

interface OrderActionsProps {
    onEdit: () => void;
    onCancel: () => void;
    onComplete?: () => void;
    onReopen?: () => void;
    onSendEmail?: () => void;
    orderData: OrderDetails;
    orderTimesCount?: number;
    hasOrganizationDetails?: boolean;
    emailCapabilityAvailable?: boolean;
}

export default function OrderActionsButton({onEdit, onCancel, onComplete, onReopen, onSendEmail, orderData, orderTimesCount = 0, hasOrganizationDetails = false, emailCapabilityAvailable = false}: OrderActionsProps) {
    const {t} = useOrdersTranslation();
    const cancelConfirmation = useConfirmationDialog();
    
    // Check preconditions for completion
    const canComplete = () => {
        // Helper must be assigned
        if (!orderData.helper) return false;
        
        // Order date must not be in the future
        if (orderData.orderDate) {
            const orderDate = new Date(orderData.orderDate);
            const today = new Date();
            
            // Set both dates to midnight for comparison
            today.setHours(0, 0, 0, 0);
            orderDate.setHours(0, 0, 0, 0);
            
            if (orderDate > today) return false;
        }
        
        // At least one activity must be booked
        return orderTimesCount !== 0;

    };


    function handleCancelClick() {
        const customConfig = {
            title: "deleteConfirmation.order.title",
            titleValues: {orderNo: orderData.orderNo},
            description: "deleteConfirmation.order.description",
            descriptionValues: {
                orderNo: orderData.orderNo,
            },
            cancelButton: {
                text: "deleteConfirmation.buttons.cancel.text",
                ariaLabel: "deleteConfirmation.buttons.cancel.ariaLabel"
            },
            confirmButton: {
                text: "deleteConfirmation.order.confirmButton.text",
                ariaLabel: "deleteConfirmation.order.confirmButton.ariaLabel",
                ariaLabelValues: {orderNo: orderData.orderNo},
                color: "orange" as const
            }
        };

        cancelConfirmation.openWithConfig(customConfig);
    }

    function handleEditClick() {
        onEdit();
    }
    
    async function handlePrintClick() {
        const apiUrl = import.meta.env.VITE_NBH_URL_ROOT || import.meta.env.VITE_API_URL || 'http://localhost:8080';
        const pdfUrl = `${apiUrl}/api/orders/${orderData.id}/pdf`;
        const token = localStorage.getItem('token');
        
        if (!token) {
            log.warn('No authentication token found');
            return;
        }
        
        try {
            const response = await fetch(pdfUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/pdf'
                }
            });
            
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                window.open(url, '_blank');
                
                // Clean up the URL after opening
                setTimeout(() => window.URL.revokeObjectURL(url), 100);
            } else {
                log.warn('Failed to fetch PDF:', response.status);
            }
        } catch (error) {
            log.warn('Error fetching PDF:', error);
        }
    }

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="px_3" data-testid="order-actions-menu">
                        <AlignJustify className="mr-2 h-4 w-4 text-gray-600 dark:text-gray-200"/>
                        <Text>{t("actions.button")}</Text>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                    {onComplete && orderData.orderStatus !== "Cancelled" && orderData.orderStatus !== "Completed" && (
                        <DropdownMenuItem 
                            onClick={onComplete} 
                            disabled={!canComplete()}
                            data-testid="order-complete-action"
                        >
                            <CheckCircle2 className="mr-2 h-4 w-4"/>
                            <Text>{t("actions.complete")}</Text>
                        </DropdownMenuItem>
                    )}
                    {onReopen && orderData.orderStatus === "Completed" && (
                        <DropdownMenuItem 
                            onClick={onReopen}
                            data-testid="order-reopen-action"
                            className="text-amber-600 dark:text-amber-400"
                        >
                            <RotateCcw className="mr-2 h-4 w-4"/>
                            <Text>{t("actions.reopen")}</Text>
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleEditClick} disabled={orderData.orderStatus === "Cancelled" || orderData.orderStatus === "Completed"}
                                      data-testid="order-edit-action">
                        <Pencil className="mr-2 h-4 w-4"/>
                        <Text>{t("actions.edit")}</Text>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={handlePrintClick}
                        disabled={!orderData.helper || !hasOrganizationDetails}
                        data-testid="order-print-action"
                        title={!hasOrganizationDetails ? t("actions.printDisabledReason") : undefined}>
                        <Printer className="mr-2 h-4 w-4"/>
                        <Text>{t("actions.print")}</Text>
                    </DropdownMenuItem>
                    {onSendEmail && emailCapabilityAvailable && (
                        <DropdownMenuItem
                            onClick={onSendEmail}
                            disabled={!orderData.helper}
                            data-testid="order-send-email-action"
                        >
                            <Mail className="mr-2 h-4 w-4"/>
                            <Text>{t("actions.sendEmail")}</Text>
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleCancelClick} className="text-red-600" disabled={orderData.orderStatus === "Cancelled"} data-testid="order-cancel-action">
                        <Trash2 className="mr-2 h-4 w-4"/>
                        <Text>{t("actions.cancel")}</Text>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            {cancelConfirmation.dialogProps && (
                <ConfirmationDialog
                    {...cancelConfirmation.dialogProps}
                    onConfirm={onCancel}
                    namespace="common"
                    testIdPrefix="cancel-order"
                />
            )}

        </>
    );
};
